import { useEffect } from 'react'
import type { Location } from 'react-router-dom'
import { NavigationType, useNavigationType } from 'react-router-dom'

type HistoryFrame = {
    pathname: string
    search: string
    key: string
}

const stack: HistoryFrame[] = []

const MAX_NAV_HISTORY_FRAMES = 64

const pruneNavStack = () => {
    const excess = stack.length - MAX_NAV_HISTORY_FRAMES
    if (excess > 0) {
        stack.splice(0, excess)
    }
}

const pathOnly = (to: string) => to.split('?')[0] || to

const frameFromLocation = (location: Location): HistoryFrame => ({
    pathname: location.pathname,
    search: location.search,
    key: location.key,
})

const framesEqualPath = (a: HistoryFrame, b: HistoryFrame) => a.pathname === b.pathname && a.search === b.search

const shouldTrackFrame = (frame: HistoryFrame) => Boolean(frame.search)

const removeStackFramesWithPathname = (pathname: string) => {
    for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].pathname === pathname) {
            stack.splice(i, 1)
        }
    }
}

export const syncNavigationHistory = (location: Location, navigationType: NavigationType) => {
    const frame = frameFromLocation(location)

    if (navigationType === NavigationType.Pop) {
        while (stack.length > 0) {
            const top = stack[stack.length - 1]
            if (top.pathname === frame.pathname && top.search === frame.search) {
                break
            }
            stack.pop()
        }
        if (stack.length === 0 && shouldTrackFrame(frame)) {
            stack.push(frame)
        }
        pruneNavStack()
        return
    }

    if (navigationType === NavigationType.Replace) {
        if (shouldTrackFrame(frame)) {
            if (stack.length === 0) {
                stack.push(frame)
            } else {
                stack[stack.length - 1] = frame
            }
        } else {
            removeStackFramesWithPathname(frame.pathname)
        }
        pruneNavStack()
        return
    }

    if (!shouldTrackFrame(frame)) {
        return
    }

    const last = stack[stack.length - 1]
    if (last && framesEqualPath(last, frame)) {
        return
    }
    stack.push(frame)
    pruneNavStack()
}

export const resolveReturnPath = (fallbackTo: string): string => {
    const targetBase = pathOnly(fallbackTo)

    for (let i = stack.length - 1; i >= 0; i--) {
        const fr = stack[i]
        if (fr.pathname === targetBase) {
            return `${fr.pathname}${fr.search}`
        }
    }

    return fallbackTo
}

export const useSyncNavigationHistory = (location: Location) => {
    const navigationType = useNavigationType()

    useEffect(() => {
        syncNavigationHistory(location, navigationType)
    }, [location, navigationType])
}
